import { createSlice } from '@reduxjs/toolkit'
import { FirestoreDatabase } from '../index'
import { Collection } from 'constants/index'
import { caseInsensitiveSort, deleteBatchDocuments } from 'utils'
import { signOut } from 'store/auth'
import { chunk, first } from 'lodash'
import { sortDataArrayByCreatedAt } from 'utils/times'
import { toast } from 'utils/notify'

export const ALL_DATES_FILTER = 'All Dates'

export const initialState = {
  error: null,
  inProgress: false,
  events: [],
  event: null,
  eventTypes: [],
  selectedDateFilter: ALL_DATES_FILTER,
  currentPage: 0,
  pageSize: 10
}

const eventSlice = createSlice({
  name: 'event',
  initialState: initialState,
  reducers: {
    setError: (state, action) => {
      state.error = action.payload
    },
    setProgress: (state, action) => {
      state.inProgress = action.payload
    },
    setEvents: (state, action) => {
      state.events = action.payload
    },
    deleteEvent: (state, action) => {
      state.events = state.events.filter((event) => event.id !== action.payload)
    },
    setEventTypes: (state, action) => {
      state.eventTypes = action.payload
    },
    setEvent: (state, action) => {
      state.event = action.payload
    },
    setSelectedDateFilter: (state, action) => {
      state.selectedDateFilter = action.payload
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload
    }
  },
  extraReducers: {
    [signOut.fulfilled]: (state) => {
      state.error = null
      state.inProgress = false
      state.events = []
      state.event = null
      state.eventTypes = []
      state.selectedDateFilter = ALL_DATES_FILTER
      state.currentPage = 0
      state.pageSize = 10
    }
  }
})

export const addEvent = (payload) => async (dispatch, getState) => {
  try {
    const user = getState().auth.user
    const doc = await FirestoreDatabase.collection(Collection.EVENTS).add({
      ...payload,
      conferenceId: user?.conferenceId
    })
    return doc.id
  } catch (err) {
    console.error(err)
    dispatch(eventSlice.actions.setError(err))
  }
}

export const fetchEvents = async (conferenceId) => {
  try {
    const eventsSnap = await FirestoreDatabase.collection(Collection.EVENTS)
      .where('conferenceId', '==', conferenceId)
      .orderBy('startDate')
      .get()

    const events = sortDataArrayByCreatedAt(
      eventsSnap.docs?.map((doc) => ({ id: doc?.id, ...doc.data() }))
    ).map((item, i) => ({ ...item, srNo: i + 1 }))
    const pollsSnap = await FirestoreDatabase.collection(Collection.POLLS)
      .where('conferenceId', '==', conferenceId)
      .get()
    const pollsData = pollsSnap.docs.map((doc) => {
      return { id: doc?.id, ...doc.data() }
    })

    const pollsEvents = []
    pollsData?.forEach((doc) => {
      if (!pollsEvents.includes(doc?.eventId)) {
        pollsEvents.push(doc?.eventId)
      }
    })

    events.forEach((event) => {
      event.havePolls = pollsEvents?.includes(event.id)
    })
    return events
  } catch (err) {
    //do nothing
  }
}

export const getConfEvents = async (conferenceId) => {
  try {
    const snapshot = await FirestoreDatabase.collection(Collection.EVENTS)
      .where('conferenceId', '==', conferenceId)
      .orderBy('startDate')
      .get()

    return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
  } catch (err) {
    console.error(err)
  }
}

export const fetchEventViaPINCode = (pinCode) => async (dispatch) => {
  try {
    const data = await FirestoreDatabase.collection(Collection.EVENTS)
      .where('pinCode', '==', pinCode)
      .limit(1)
      .get()
    const doc = first(data?.docs)
    return { id: doc.id, ...doc.data() }
  } catch (err) {
    dispatch(eventSlice.actions.setError(err))
  }
}

export const fetchEventTypes = () => async (dispatch, getState) => {
  try {
    dispatch(eventSlice.actions.setProgress(true))
    const user = getState().auth.user
    FirestoreDatabase.collection(Collection.EVENT_CARD_STYLES)
      .where('conferenceId', '==', user?.conferenceId)
      .onSnapshot((documents) => {
        const eventTypes = []
        documents.forEach((doc) => {
          eventTypes.push({
            id: doc.id,
            ...doc.data()
          })
        })
        dispatch(eventSlice.actions.setEventTypes(caseInsensitiveSort(eventTypes, 'type')))
        dispatch(eventSlice.actions.setProgress(false))
      })
  } catch (err) {
    dispatch(eventSlice.actions.setError(err))
    dispatch(eventSlice.actions.setProgress(false))
  }
}

export const getCardColors = async (conferenceId) => {
  try {
    const snapshot = await FirestoreDatabase.collection(Collection.EVENT_CARD_STYLES)
      .where('conferenceId', '==', conferenceId)
      .get()

    const colors = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data()
    }))
    return caseInsensitiveSort(colors, 'type')
  } catch (err) {
    console.error(err)
  }
}

export const fetchRealTimeEvent = (eventId, callback) => {
  return FirestoreDatabase.collection(Collection.EVENTS)
    .doc(eventId)
    .onSnapshot((doc) => {
      const event = doc.exists ? { id: doc.id, ...doc.data() } : null
      callback(event)
    })
}

export const fetchEventById = (eventId) => async (dispatch) => {
  try {
    dispatch(eventSlice.actions.setProgress(true))
    const snapshot = await FirestoreDatabase.collection(Collection.EVENTS).doc(eventId).get()
    dispatch(
      eventSlice.actions.setEvent({
        id: snapshot.id,
        ...snapshot.data()
      })
    )
    dispatch(eventSlice.actions.setProgress(false))
  } catch (err) {
    dispatch(eventSlice.actions.setError(err))
    dispatch(eventSlice.actions.setProgress(false))
  }
}

export const removeSessionsAndTalks = async (eventId) => {
  try {
    const sessionsSnap = await FirestoreDatabase.collection(Collection.SESSIONS)
      .where('eventId', '==', eventId)
      .get()
    const sessions = sessionsSnap.docs.map((doc) => ({ id: doc?.id, ...doc.data() }))
    const sessionIdsList = sessions.map((session) => session?.id)

    const batchSize = 10
    const batchedSessionIds = chunk(sessionIdsList, batchSize)

    const sessionTalks = []
    for (const batchSessionIds of batchedSessionIds) {
      const talksSnap = await FirestoreDatabase.collection(Collection.TALKS)
        .where('sessionId', 'in', batchSessionIds)
        .get()
      talksSnap.docs.forEach((doc) => {
        sessionTalks.push({ id: doc?.id, ...doc.data() })
      })
    }

    const pollIdsList = sessionTalks
      .filter((talk) => talk.havePolls === true)
      .flatMap((talk) => talk?.pollIds ?? (talk?.pollId ? [talk.pollId] : []))
    const pollsColRef = FirestoreDatabase.collection(Collection.POLLS)

    sessions.forEach((session) => {
      if (session?.havePolls) {
        pollIdsList.push(...(session?.pollIds ?? (session?.pollId ? [session.pollId] : [])))
      }
    })

    const pollIdsChunks = chunk(pollIdsList, 500) // firebase allows us to commit a batch of max 500 documents
    const promises = pollIdsChunks.map((chunk) => {
      const batch = FirestoreDatabase.batch()
      chunk?.forEach((pollId) => {
        const docRef = pollsColRef.doc(pollId)
        batch.update(docRef, {
          alreadyUsed: false,
          eventId: null,
          sessionId: null,
          talkId: null
        })
      })
      return batch.commit()
    })
    await Promise.all(promises)

    const talkRefs = sessionTalks.map(({ id }) =>
      FirestoreDatabase.collection(Collection.TALKS).doc(id)
    )
    const sessionRefs = sessions.map(({ id }) =>
      FirestoreDatabase.collection(Collection.SESSIONS).doc(id)
    )
    await Promise.all([deleteBatchDocuments(talkRefs), deleteBatchDocuments(sessionRefs)])
  } catch (err) {
    console.error(err)
  }
}

export const removeEventTalks = async (eventId) => {
  try {
    const talksSnap = await FirestoreDatabase.collection(Collection.TALKS)
      .where('eventId', '==', eventId)
      .get()
    const talks = talksSnap.docs.map((doc) => ({ id: doc?.id, ...doc.data() }))

    const pollIdsList = talks
      .filter((talk) => talk.havePolls === true)
      .flatMap((talk) => talk?.pollIds ?? (talk?.pollId ? [talk.pollId] : []))
    const pollsColRef = FirestoreDatabase.collection(Collection.POLLS)

    const pollIdsChunks = chunk(pollIdsList, 500) // firebase allows us to commit a batch of max 500 documents
    const promises = pollIdsChunks.map((chunk) => {
      const batch = FirestoreDatabase.batch()
      chunk?.forEach((pollId) => {
        const docRef = pollsColRef.doc(pollId)
        batch.update(docRef, {
          alreadyUsed: false,
          eventId: null,
          sessionId: null,
          talkId: null
        })
      })
      return batch.commit()
    })
    await Promise.all(promises)

    const talksRefs = talks.map(({ id }) => FirestoreDatabase.collection(Collection.TALKS).doc(id))
    await deleteBatchDocuments(talksRefs)
  } catch (err) {
    console.error(err)
  }
}

export const removeAbstractOrPosterNumberFromEventTalks = async (eventId) => {
  try {
    const talksSnap = await FirestoreDatabase.collection(Collection.TALKS)
      .where('eventId', '==', eventId)
      .get()
    const talks = talksSnap.docs.map((doc) => ({ id: doc?.id, ...doc.data() }))

    const talkIdsList = talks.map((talk) => talk.id)
    const talksColRef = FirestoreDatabase.collection(Collection.TALKS)

    const talkIdsChunks = chunk(talkIdsList, 500) // firebase allows us to commit a batch of max 500 documents
    const promises = talkIdsChunks.map((chunk) => {
      const batch = FirestoreDatabase.batch()
      chunk?.forEach((talkId) => {
        const docRef = talksColRef.doc(talkId)
        batch.update(docRef, {
          abstractNumber: null,
          posterNumber: null,
          isAbstract: false
        })
      })
      return batch.commit()
    })
    await Promise.all(promises)
  } catch (err) {
    console.error(err)
  }
}

export const updateEventById = (eventId, payload) => async (dispatch) => {
  try {
    await FirestoreDatabase.collection(Collection.EVENTS).doc(eventId).update(payload)
    return true
  } catch (err) {
    dispatch(eventSlice.actions.setError(err))
  }
}

export const deleteEventById = (eventId) => async (dispatch) => {
  try {
    dispatch(eventSlice.actions.setProgress(true))

    const eventSnap = await FirestoreDatabase.collection(Collection.EVENTS).doc(eventId).get()
    const event = { id: eventSnap.id, ...eventSnap.data() }
    const batch = FirestoreDatabase.batch()
    // Delete event attachments if any
    const eventAttachmentsSnapshot = await FirestoreDatabase.collection(Collection.EVENTS)
      .doc(eventId)
      .collection(Collection.ATTACHMENTS)
      .get()

    // Delete each event attachment document
    const eventAttachmentRefs = eventAttachmentsSnapshot.docs.map((doc) => {
      return FirestoreDatabase.collection(Collection.EVENTS)
        .doc(eventId)
        .collection(Collection.ATTACHMENTS)
        .doc(doc.id)
    })

    const eventPollIds = event?.pollIds ?? (event?.pollId ? [event.pollId] : [])
    if (eventPollIds.length) {
      const pollsColRef = FirestoreDatabase.collection(Collection.POLLS)
      eventPollIds.forEach((pollId) => {
        const docRef = pollsColRef.doc(pollId)
        batch.update(docRef, {
          alreadyUsed: false,
          eventId: null,
          sessionId: null,
          talkId: null
        })
      })
    }
    await deleteBatchDocuments(eventAttachmentRefs)
    await deleteAllSubEventsOfEvent(eventId)
    const eventDoc = FirestoreDatabase.collection(Collection.EVENTS).doc(eventId)
    batch.delete(eventDoc)

    await batch.commit()
    dispatch(eventSlice.actions.deleteEvent(eventId))
    dispatch(eventSlice.actions.setProgress(false))
  } catch (err) {
    dispatch(eventSlice.actions.setError(err))
    dispatch(eventSlice.actions.setProgress(false))
  }
}

export const deleteAllSubEventsOfEvent = async (eventId) => {
  try {
    // Get all sessions (sub-events) associated with the event
    const sessionsSnap = await FirestoreDatabase.collection(Collection.SESSIONS)
      .where('eventId', '==', eventId)
      .get()

    if (sessionsSnap.empty) {
      toast.info('No sub-events to delete.')
      return 0
    }

    const sessions = sessionsSnap.docs.map((doc) => ({
      id: doc.id,
      ...doc.data()
    }))

    const sessionIds = sessions.map((session) => session.id)
    const sessionIdChunks = chunk(sessionIds, 10)
    const talks = []

    // Second: Fetch talks where sessionId is in each chunk
    for (const chunkedIds of sessionIdChunks) {
      const sessionTalksSnap = await FirestoreDatabase.collection(Collection.TALKS)
        .where('sessionId', 'in', chunkedIds)
        .get()

      sessionTalksSnap.forEach((doc) => {
        const data = { id: doc.id, ...doc.data() }

        // Avoid duplication in case both conditions match the same doc
        if (!talks.some((t) => t.id === data.id)) {
          talks.push(data)
        }
      })
    }

    // Collect all poll IDs from sessions and talks
    const pollIdsListWithinSessions = sessions
      .filter((session) => session.havePolls === true)
      .flatMap((session) => session.pollIds ?? (session.pollId ? [session.pollId] : []))

    const pollIdsListWithinTalks = talks
      .filter((talk) => talk.havePolls === true)
      .flatMap((talk) => talk?.pollIds ?? (talk?.pollId ? [talk.pollId] : []))

    const allPollIds = [...pollIdsListWithinSessions, ...pollIdsListWithinTalks].filter(Boolean)

    // Create a batch to handle all operations
    const batch = FirestoreDatabase.batch()

    // Update polls to remove associations
    const pollsColRef = FirestoreDatabase.collection(Collection.POLLS)
    allPollIds.forEach((pollId) => {
      const docRef = pollsColRef.doc(pollId)
      batch.update(docRef, {
        alreadyUsed: false,
        eventId: null,
        sessionId: null,
        talkId: null
      })
    })

    // For each talk, delete its attachments first
    for (const talk of talks) {
      // Delete talk attachments
      const talkAttachmentsSnapshot = await FirestoreDatabase.collection(Collection.TALKS)
        .doc(talk.id)
        .collection(Collection.ATTACHMENTS)
        .get()

      // Delete each talk attachment document
      const talkAttachmentRefs = talkAttachmentsSnapshot.docs.map((doc) => {
        return FirestoreDatabase.collection(Collection.TALKS)
          .doc(talk.id)
          .collection(Collection.ATTACHMENTS)
          .doc(doc.id)
      })

      await deleteBatchDocuments(talkAttachmentRefs)

      // Delete talk document
      const docRef = FirestoreDatabase.collection(Collection.TALKS).doc(talk.id)
      batch.delete(docRef)
    }

    // For each session, delete its attachments first
    for (const session of sessions) {
      // Delete session attachments
      const sessionAttachmentsSnapshot = await FirestoreDatabase.collection(Collection.SESSIONS)
        .doc(session.id)
        .collection(Collection.ATTACHMENTS)
        .get()

      // Delete each session attachment document
      const sessionAttachmentRefs = sessionAttachmentsSnapshot.docs.map((doc) => {
        return FirestoreDatabase.collection(Collection.SESSIONS)
          .doc(session.id)
          .collection(Collection.ATTACHMENTS)
          .doc(doc.id)
      })
      await deleteBatchDocuments(sessionAttachmentRefs)
      // Delete session document
      const docRef = FirestoreDatabase.collection(Collection.SESSIONS).doc(session.id)
      batch.delete(docRef)
    }

    // Commit all operations
    await batch.commit()
    return sessions.length
  } catch (error) {
    console.error('Error deleting sub-events:', error)
    throw error
  }
}

export const resetEventStore = () => (dispatch) => {
  try {
    dispatch(eventSlice.actions.setEvent(null))
  } catch (err) {
    dispatch(eventSlice.actions.setError(err))
  }
}

// Update sub-events with either venue, dates, or both based on provided changes
export const updateSubEvents = async (eventId, { venueId, startDate, endDate }) => {
  try {
    // Check what needs to be updated
    const venueUpdates = venueId !== undefined
    const dateUpdates = startDate !== undefined && endDate !== undefined

    if (!venueUpdates && !dateUpdates) {
      return true // No updates to make
    }

    // Get all sessions for this event in a single query
    const sessionsSnapshot = await FirestoreDatabase.collection(Collection.SESSIONS)
      .where('eventId', '==', eventId)
      .get()

    if (sessionsSnapshot.empty) {
      return true // No sessions to update
    }

    // Collect updates for each session
    const sessionUpdates = []
    const sessionsToCheckForTalks = [] // Track sessions that were updated and might have talks
    let venueUpdateCount = 0
    let dateUpdateCount = 0
    let talkVenueUpdateCount = 0
    let talkDateUpdateCount = 0

    sessionsSnapshot.docs.forEach((doc) => {
      const sessionData = doc.data()
      const updateData = {}
      const sessionInfo = {
        id: doc.id,
        updatedVenue: false,
        updatedTime: false,
        venueId: null,
        startDate: null,
        endDate: null
      }

      // Check if session inherits venue and we have venue updates
      if (venueUpdates && sessionData.inheritVenue === true) {
        updateData.venueId = venueId
        sessionInfo.updatedVenue = true
        sessionInfo.venueId = venueId
        venueUpdateCount++
      }

      // Check if session inherits time and we have date updates
      if (dateUpdates && sessionData.inheritTime === true) {
        updateData.startDate = startDate
        updateData.endDate = endDate
        sessionInfo.updatedTime = true
        sessionInfo.startDate = startDate
        sessionInfo.endDate = endDate
        dateUpdateCount++
      }

      // Only add if there's something to update
      if (Object.keys(updateData).length > 0) {
        sessionUpdates.push({
          id: doc.id,
          updates: updateData
        })

        // Add to the list of sessions to check for talks
        if (sessionInfo.updatedVenue || sessionInfo.updatedTime) {
          sessionsToCheckForTalks.push(sessionInfo)
        }
      }
    })

    // Skip batching if no updates to make
    if (sessionUpdates.length === 0) {
      return true
    }

    // Split sessions into chunks of 500 (Firestore batch limit)
    const batchChunks = chunk(sessionUpdates, 500)
    const batchPromises = batchChunks.map((batchChunk) => {
      const batch = FirestoreDatabase.batch()

      batchChunk.forEach(({ id, updates }) => {
        const sessionRef = FirestoreDatabase.collection(Collection.SESSIONS).doc(id)
        batch.update(sessionRef, updates)
      })

      return batch.commit()
    })

    // Execute all session batches
    await Promise.all(batchPromises)

    // Now update talks that inherit from updated sessions
    if (sessionsToCheckForTalks.length > 0) {
      // Process sessions in chunks to avoid firestore limits on 'in' queries
      const sessionIdChunks = chunk(
        sessionsToCheckForTalks.map((session) => session.id),
        10 // Using smaller chunks for 'in' queries
      )

      // Create a map for quick lookup of session info by ID
      const sessionInfoMap = {}
      sessionsToCheckForTalks.forEach((session) => {
        sessionInfoMap[session.id] = session
      })

      // Collect all talk updates across all chunks
      const talkUpdates = []

      // Process each chunk of session IDs
      for (const sessionIdsChunk of sessionIdChunks) {
        const talksSnapshot = await FirestoreDatabase.collection(Collection.TALKS)
          .where('sessionId', 'in', sessionIdsChunk)
          .get()

        talksSnapshot.docs.forEach((doc) => {
          const talkData = doc.data()
          const sessionInfo = sessionInfoMap[talkData.sessionId]
          const updateData = {}

          // Check if talk inherits venue from session and session venue was updated
          if (sessionInfo.updatedVenue && talkData.inheritVenue === true) {
            updateData.venueId = sessionInfo.venueId
            talkVenueUpdateCount++
          }

          // Check if talk inherits time from session and session time was updated
          if (sessionInfo.updatedTime && talkData.inheritTime === true) {
            updateData.startDate = sessionInfo.startDate
            updateData.endDate = sessionInfo.endDate
            talkDateUpdateCount++
          }

          // Only add if there's something to update
          if (Object.keys(updateData).length > 0) {
            talkUpdates.push({
              id: doc.id,
              updates: updateData
            })
          }
        })
      }

      // Update talks in batches if there are any updates
      if (talkUpdates.length > 0) {
        const talkBatchChunks = chunk(talkUpdates, 500)
        const talkBatchPromises = talkBatchChunks.map((batchChunk) => {
          const batch = FirestoreDatabase.batch()

          batchChunk.forEach(({ id, updates }) => {
            const talkRef = FirestoreDatabase.collection(Collection.TALKS).doc(id)
            batch.update(talkRef, updates)
          })

          return batch.commit()
        })

        // Execute all talk batches
        await Promise.all(talkBatchPromises)
      }
    }

    // Show appropriate success message for sessions and talks
    let sessionMessage = ''
    let talkMessage = ''

    // Session update message
    if (venueUpdateCount > 0 && dateUpdateCount > 0) {
      sessionMessage = `Updated venue for ${venueUpdateCount} and dates for ${dateUpdateCount} sub-events`
    } else if (venueUpdateCount > 0) {
      sessionMessage = `Updated venue for ${venueUpdateCount} sub-events`
    } else if (dateUpdateCount > 0) {
      sessionMessage = `Updated dates for ${dateUpdateCount} sub-events`
    }

    // Talk update message
    if (talkVenueUpdateCount > 0 && talkDateUpdateCount > 0) {
      talkMessage = `Updated venue for ${talkVenueUpdateCount} and dates for ${talkDateUpdateCount} posters/talks`
    } else if (talkVenueUpdateCount > 0) {
      talkMessage = `Updated venue for ${talkVenueUpdateCount} posters/talks`
    } else if (talkDateUpdateCount > 0) {
      talkMessage = `Updated dates for ${talkDateUpdateCount} posters/talks`
    }

    // Display messages
    if (sessionMessage && talkMessage) {
      toast.success(`${sessionMessage}; ${talkMessage}`)
    } else if (sessionMessage) {
      toast.success(sessionMessage)
    } else if (talkMessage) {
      toast.success(talkMessage)
    }

    return true
  } catch (error) {
    console.error('Error updating sub-events and posters/talks:', error)
    toast.error('Could not update sub-events and talks. Try again.')
    return false
  }
}

// Update all talks directly associated with an event (inheriting venue/time from the event)
export const updateEventTalks = async (eventId, { venueId, startDate, endDate }) => {
  try {
    // Check what needs to be updated
    const venueUpdates = venueId !== undefined
    const dateUpdates = startDate !== undefined && endDate !== undefined

    if (!venueUpdates && !dateUpdates) {
      return true // No updates to make
    }

    // Get all talks for this event in a single query
    const talksSnapshot = await FirestoreDatabase.collection(Collection.TALKS)
      .where('eventId', '==', eventId)
      .get()

    if (talksSnapshot.empty) {
      return true // No talks to update
    }

    // Counters for tracking updates
    let venueUpdateCount = 0
    let dateUpdateCount = 0
    const talkUpdates = []

    // Check each talk for inheritance properties and collect updates
    talksSnapshot.docs.forEach((doc) => {
      const talkData = doc.data()
      const updateData = {}

      // Check if talk inherits venue and we have venue updates
      if (venueUpdates && talkData.inheritVenue === true) {
        updateData.venueId = venueId
        venueUpdateCount++
      }

      // Check if talk inherits time and we have date updates
      if (dateUpdates && talkData.inheritTime === true) {
        updateData.startDate = startDate
        updateData.endDate = endDate
        dateUpdateCount++
      }

      // Only add if there's something to update
      if (Object.keys(updateData).length > 0) {
        talkUpdates.push({
          id: doc.id,
          updates: updateData
        })
      }
    })

    // Skip batching if no updates to make
    if (talkUpdates.length === 0) {
      return true
    }

    // Split into chunks of 500 (Firestore batch limit)
    const batchChunks = chunk(talkUpdates, 500)
    const batchPromises = batchChunks.map((batchChunk) => {
      const batch = FirestoreDatabase.batch()

      batchChunk.forEach(({ id, updates }) => {
        const talkRef = FirestoreDatabase.collection(Collection.TALKS).doc(id)
        batch.update(talkRef, updates)
      })

      return batch.commit()
    })

    // Execute all batches
    await Promise.all(batchPromises)

    // Show appropriate success message
    if (venueUpdateCount > 0 && dateUpdateCount > 0) {
      toast.success(
        `Updated venue for ${venueUpdateCount} and dates for ${dateUpdateCount} posters/talks associated to this event`
      )
    } else if (venueUpdateCount > 0) {
      toast.success(`Updated venue for ${venueUpdateCount} posters/talks associated to this event`)
    } else if (dateUpdateCount > 0) {
      toast.success(`Updated dates for ${dateUpdateCount} posters/talks associated to this event`)
    }

    return true
  } catch (error) {
    console.error('Error updating event posters/talks:', error)
    toast.error('Could not update event talks. Try again.')
    return false
  }
}

export default eventSlice
